import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';

import useScopedLocalStorage from './localStorage/useScopedLocalStorage';

type ToggleConfig = {
  localStorageKey?: string;
};

// only persist value for now, but state could be added if needed
const LS_AFFIXES = {
  VALUE: 'value',
} as const;

export type UseToggle<T = unknown> = {
  hide: () => void;
  inverse: (stateObject?: T) => void;
  setValue: Dispatch<SetStateAction<boolean>>;
  show: (stateObject?: T) => void;
  state: T | null;
  value: boolean;
};

// keeping initialValue first param for backwards compatibility
const useToggle = <T>(initialValue?: boolean, config?: ToggleConfig): UseToggle<T> => {
  const [state, setState] = useState<T | null>(null);

  const [value, setValue] = useScopedLocalStorage(
    config?.localStorageKey ? `${config.localStorageKey}-${LS_AFFIXES.VALUE}` : null,
    initialValue ?? false,
  );

  const showToggle = useCallback(
    (stateObject?: T) => {
      setValue(true);
      setState(stateObject ?? null);
    },
    [setValue],
  );

  const hideToggle = useCallback(() => {
    setValue(false);
    setState(null);
  }, [setValue]);

  const inverseToggle = useCallback(
    (stateObject?: T) => {
      if (value) {
        hideToggle();
        return;
      }
      showToggle(stateObject);
    },
    [hideToggle, showToggle, value],
  );

  const toggle = useMemo(
    () => ({
      hide: hideToggle,
      inverse: inverseToggle,
      setValue,
      show: showToggle,
      state,
      value,
    }),
    [hideToggle, inverseToggle, showToggle, setValue, state, value],
  );

  return toggle;
};

export default useToggle;
